import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { warehouseOptionLabel } from '@/lib/warehouseLabel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ProductSelect, useProductCatalog } from '@/components/products';
import { BatchSelect } from '@/components/batches';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';

interface Warehouse {
  id: string;
  code: string;
  name: string;
  type: 'HUB' | 'SPOKE';
  parentWarehouseId?: string | null;
  childWarehouses?: Warehouse[];
}

interface BatchOption {
  id: string;
  batchNumber: string;
  quantityRemaining: number;
  expiryDate: string | null;
}

interface TransferLine {
  productId: string;
  batchNumber: string;
  quantity: string;
}

function parseWarehouseResponse(data: unknown): Warehouse[] {
  if (Array.isArray(data)) return data as Warehouse[];
  if (data && typeof data === 'object' && 'items' in data) {
    const obj = data as { items?: unknown };
    return Array.isArray(obj.items) ? (obj.items as Warehouse[]) : [];
  }
  return [];
}

export default function TransferFormPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toWarehouseIdParam = searchParams.get('toWarehouseId');
  const { products, loading: productsLoading, error: productsError } = useProductCatalog();

  const [hubs, setHubs] = useState<Warehouse[]>([]);
  const [destinations, setDestinations] = useState<Warehouse[]>([]);
  const [fromWarehouseId, setFromWarehouseId] = useState('');
  const [toWarehouseId, setToWarehouseId] = useState(toWarehouseIdParam || '');
  const [transferDate, setTransferDate] = useState(() =>
    new Date().toISOString().split('T')[0]
  );
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<TransferLine[]>([
    { productId: '', batchNumber: '', quantity: '' },
  ]);
  const [batchesByProduct, setBatchesByProduct] = useState<Record<string, BatchOption[]>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadHubs = useCallback(async () => {
    try {
      const hubsRes = await apiFetch('/api/v1/transfers/sources');
      if (!hubsRes.ok) {
        setError(
          hubsRes.status === 403
            ? 'You do not have permission to load hubs for transfers'
            : 'Failed to load warehouses'
        );
        setHubs([]);
        return;
      }
      const data = await hubsRes.json();
      const hubList = parseWarehouseResponse(data);
      setHubs(hubList);
      if (hubList.length === 0) {
        setError(
          'No source hubs available. Ask an admin to assign your hub under Users → Locations (spoke-only assignments need a parent hub linked).'
        );
      } else if (hubList.length === 1) {
        setFromWarehouseId(hubList[0].id);
      }
    } catch {
      setError('Failed to load warehouses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadHubs();
  }, [loadHubs]);

  useEffect(() => {
    if (productsError) {
      setError(productsError);
    }
  }, [productsError]);

  const loadDestinations = useCallback(async (hubId: string) => {
    if (!hubId) {
      setDestinations([]);
      return;
    }
    try {
      const res = await apiFetch(
        `/api/v1/transfers/destinations?fromWarehouseId=${encodeURIComponent(hubId)}`
      );
      if (res.ok) {
        const data = await res.json();
        setDestinations(parseWarehouseResponse(data));
      } else {
        setDestinations([]);
      }
    } catch {
      setDestinations([]);
    }
  }, []);

  useEffect(() => {
    if (fromWarehouseId) {
      void loadDestinations(fromWarehouseId);
      setBatchesByProduct({});
    } else {
      setDestinations([]);
      setBatchesByProduct({});
    }
  }, [fromWarehouseId, loadDestinations]);

  useEffect(() => {
    if (toWarehouseIdParam && hubs.length > 0 && !fromWarehouseId) {
      apiFetch(`/api/v1/warehouses/${toWarehouseIdParam}`)
        .then((res) => {
          if (res.ok) return res.json();
          return null;
        })
        .then((wh) => {
          const parentId = wh?.parentWarehouseId ?? wh?.parentWarehouse?.id;
          if (parentId) {
            setFromWarehouseId(parentId);
          }
        })
        .catch(() => {});
    }
  }, [toWarehouseIdParam, hubs.length, fromWarehouseId]);

  const loadBatchesForProduct = useCallback(
    async (productId: string) => {
      if (!fromWarehouseId || !productId || batchesByProduct[productId]) return;

      try {
        const res = await apiFetch(
          `/api/v1/batches/product/${productId}?warehouseId=${fromWarehouseId}`
        );
        if (res.ok) {
          const data = await res.json();
          setBatchesByProduct((prev) => ({
            ...prev,
            [productId]: Array.isArray(data) ? data : [],
          }));
        }
      } catch {
        // Non-blocking — operator can still type/scan batch number
      }
    },
    [fromWarehouseId, batchesByProduct]
  );

  const addLine = () => {
    setLines((prev) => [...prev, { productId: '', batchNumber: '', quantity: '' }]);
  };

  const removeLine = (idx: number) => {
    setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
  };

  const updateLine = (idx: number, field: keyof TransferLine, value: string) => {
    setLines((prev) =>
      prev.map((line, i) => (i === idx ? { ...line, [field]: value } : line))
    );
    if (field === 'productId' && value) {
      void loadBatchesForProduct(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!fromWarehouseId) {
      setError('Select a source hub before adding product lines.');
      return;
    }

    const validLines = lines
      .filter(
        (l) =>
          l.productId &&
          l.batchNumber.trim() &&
          l.quantity &&
          parseInt(l.quantity, 10) > 0
      )
      .map((l) => ({
        productId: l.productId,
        batchNumber: l.batchNumber.trim(),
        quantity: parseInt(l.quantity, 10),
      }));

    if (!toWarehouseId || !transferDate || validLines.length === 0) {
      setError(
        'Please fill in source hub, destination spoke, date, and at least one complete line (product, batch, qty).'
      );
      return;
    }

    if (fromWarehouseId === toWarehouseId) {
      setError('Source and destination must be different.');
      return;
    }

    setSaving(true);
    try {
      const res = await apiFetch('/api/v1/transfers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromWarehouseId,
          toWarehouseId,
          transferDate: new Date(transferDate).toISOString(),
          lines: validLines,
          notes: notes.trim() || undefined,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? 'Failed to create transfer');
      }
      const transfer = await res.json();
      navigate(`/locations/transfers/${transfer.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create transfer');
    } finally {
      setSaving(false);
    }
  };

  if (loading || productsLoading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <Button
          variant="ghost"
          className="mb-4"
          onClick={() => navigate('/locations/transfers')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Transfers
        </Button>

        <Card>
          <CardHeader>
            <CardTitle>New Stock Transfer</CardTitle>
            <CardDescription>
              Ship from a hub to one of its spokes, or to another hub. Scan or enter the
              supplier batch number for each carton — stock is validated against that exact
              batch at the source hub (not automatic FIFO). Destination confirms receipt.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                  {error}
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="fromWarehouse">From (Hub) *</Label>
                  <Select
                    value={fromWarehouseId}
                    onValueChange={(v) => {
                      setFromWarehouseId(v);
                      setToWarehouseId('');
                      setLines([{ productId: '', batchNumber: '', quantity: '' }]);
                    }}
                    required
                  >
                    <SelectTrigger id="fromWarehouse">
                      <SelectValue placeholder="Select hub" />
                    </SelectTrigger>
                    <SelectContent>
                      {hubs.map((w) => (
                        <SelectItem key={w.id} value={w.id}>
                          {warehouseOptionLabel(w)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="toWarehouse">To (Spoke or Hub) *</Label>
                  <Select
                    value={toWarehouseId}
                    onValueChange={setToWarehouseId}
                    required
                    disabled={!fromWarehouseId}
                  >
                    <SelectTrigger id="toWarehouse">
                      <SelectValue placeholder="Select destination" />
                    </SelectTrigger>
                    <SelectContent>
                      {destinations.map((w) => (
                        <SelectItem key={w.id} value={w.id}>
                          {warehouseOptionLabel(w)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="transferDate">Transfer Date *</Label>
                <Input
                  id="transferDate"
                  type="date"
                  value={transferDate}
                  onChange={(e) => setTransferDate(e.target.value)}
                  required
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Pack lines *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addLine}
                    disabled={!fromWarehouseId}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add line
                  </Button>
                </div>
                {!fromWarehouseId && (
                  <p className="text-sm text-muted-foreground mb-3">
                    Select a source hub first to load available batches.
                  </p>
                )}
                <div className="space-y-3">
                  {lines.map((line, idx) => {
                    const productBatches = line.productId
                      ? batchesByProduct[line.productId] ?? []
                      : [];
                    const matchedBatch = line.batchNumber.trim()
                      ? productBatches.find(
                          (b) =>
                            b.batchNumber.toLowerCase() ===
                            line.batchNumber.trim().toLowerCase()
                        )
                      : undefined;

                    return (
                      <div
                        key={idx}
                        className="flex flex-wrap gap-3 items-end p-3 rounded-md border bg-muted/30"
                      >
                        <div className="flex-1 min-w-[180px]">
                          <Label className="text-xs">Product</Label>
                          <ProductSelect
                            value={line.productId}
                            onValueChange={(v) => updateLine(idx, 'productId', v)}
                            products={products}
                            disabled={!fromWarehouseId}
                          />
                        </div>
                        <div className="w-56 min-w-[14rem]">
                          <Label className="text-xs">Batch *</Label>
                          <BatchSelect
                            value={line.batchNumber}
                            onValueChange={(v) => updateLine(idx, 'batchNumber', v)}
                            batches={productBatches}
                            disabled={!line.productId || !fromWarehouseId}
                            placeholder={
                              productBatches.length > 0
                                ? 'Search or scan batch…'
                                : 'Scan batch #'
                            }
                          />
                        </div>
                        <div className="w-24">
                          <Label className="text-xs">Qty</Label>
                          <Input
                            type="number"
                            min={1}
                            max={matchedBatch?.quantityRemaining}
                            value={line.quantity}
                            onChange={(e) => updateLine(idx, 'quantity', e.target.value)}
                            placeholder="0"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeLine(idx)}
                          disabled={lines.length === 1}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label htmlFor="notes">Notes (optional)</Label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional details..."
                  rows={2}
                  maxLength={500}
                  className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/locations/transfers')}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saving || !fromWarehouseId}>
                  {saving ? 'Creating...' : 'Create transfer'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
